import React, { useEffect, useState } from "react";
import { useScroll, useTransform } from "framer-motion";
import { motionSafe as motion } from "@/lib/motionSafe";
import { useLenis } from "lenis/react";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useI18n } from "../../i18n";
import { CraDeadlineCountdown } from "./CraDeadlineCountdown";
import { BOOKING_URL } from "../../lib/booking";

const HERO_ART = "https://customer-assets-lqy194kg.emergentagent.net/job_compliance-hub-863/artifacts/b4k4nr0q_image.png";

const line = {
  hidden: { y: "12%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] } }),
};

// Animated circuit-network background — signals converging from the left onto
// a soft glowing focal point on the right ("law meets technology meets human").
// Kept intentionally subtle (low opacity, slow motion) so it never competes
// with the headline typography.
const CircuitNetwork = () => {
  // Line paths — horizontal signals bending toward the convergence point (~x=760).
  const paths = [
    "M0 90 L200 90 L280 170 L520 170 L600 260 L760 260",
    "M0 170 L280 170 L400 170 L520 170",
    "M0 250 L360 250 L440 190 L600 190 L680 260 L760 260",
    "M0 330 L240 330 L340 260 L520 260 L600 260",
    "M0 410 L200 410 L320 330 L520 330 L600 260",
    "M0 480 L160 480 L260 400 L440 400 L560 320 L760 320",
  ];
  // Nodes to place along the network (drawn on top of lines).
  const nodes = [
    { x: 200, y: 90 }, { x: 280, y: 170 }, { x: 520, y: 170 },
    { x: 360, y: 250 }, { x: 440, y: 190 }, { x: 600, y: 190 },
    { x: 240, y: 330 }, { x: 340, y: 260 }, { x: 520, y: 260 },
    { x: 320, y: 330 }, { x: 520, y: 330 }, { x: 600, y: 260 },
    { x: 260, y: 400 }, { x: 440, y: 400 }, { x: 560, y: 320 },
    { x: 680, y: 260 }, { x: 760, y: 260 }, { x: 760, y: 320 },
  ];

  return (
    <svg
      viewBox="0 0 800 560"
      preserveAspectRatio="xMidYMid slice"
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="node-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="focus-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#0052FF" stopOpacity="0.55" />
          <stop offset="45%" stopColor="#0052FF" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#0052FF" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="line-fade" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0" />
          <stop offset="20%" stopColor="#3B82F6" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#60A5FA" stopOpacity="0.9" />
        </linearGradient>
      </defs>

      {/* Convergence glow — where "law · tech · human" meet */}
      <circle cx="760" cy="290" r="220" fill="url(#focus-glow)" />

      {/* Circuit lines */}
      <g fill="none" stroke="url(#line-fade)" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round">
        {paths.map((d, i) => (
          <motion.path
            key={i}
            d={d}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.7 }}
            transition={{ duration: 2.2, delay: 0.25 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
          />
        ))}
      </g>

      {/* Nodes with soft glow + pulsing pulse */}
      <g>
        {nodes.map((n, i) => (
          <g key={i}>
            <motion.circle
              cx={n.x} cy={n.y} r="10"
              fill="url(#node-glow)"
              initial={{ opacity: 1 }}
              animate={{ opacity: [0.15, 0.55, 0.15] }}
              transition={{ duration: 3 + (i % 4) * 0.6, delay: 1.2 + (i % 6) * 0.25, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle
              cx={n.x} cy={n.y} r="2.5"
              fill="#60A5FA"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1 + i * 0.05 }}
            />
          </g>
        ))}
      </g>

      {/* Focal node at the convergence point */}
      <motion.circle
        cx="760" cy="290" r="6"
        fill="#ffffff"
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 1.8 }}
      />
      <motion.circle
        cx="760" cy="290" r="18"
        fill="none" stroke="#0052FF" strokeWidth="1"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: [0, 0.7, 0], scale: [0.6, 2.6, 3.4] }}
        transition={{ duration: 3, delay: 2.2, repeat: Infinity, ease: "easeOut" }}
      />
    </svg>
  );
};

export const Hero = () => {
  const { t } = useI18n();
  const lenis = useLenis();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 600], [0, 160]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { duration: 1.1, lock: true });
    else el.scrollIntoView({ block: "start" });
  };

  // Defer the 2.7 MB decorative hero PNG until the browser is idle.
  // Massive FCP/LCP win: text + CSS + fonts paint immediately, the image
  // fades in ~1s later — which is invisible to users because the image is
  // heavily masked and 40 % blue-overlaid anyway.
  const [imgReady, setImgReady] = useState(false);
  useEffect(() => {
    const idle =
      window.requestIdleCallback ||
      ((cb) => setTimeout(cb, 900));
    const cancel = window.cancelIdleCallback || clearTimeout;
    const id = idle(() => setImgReady(true), { timeout: 1200 });
    return () => cancel(id);
  }, []);

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Parallax layered background */}
      <motion.div style={{ y: yBg, opacity }} className="absolute inset-0 z-0">
        <div className="absolute inset-0 grid-lines opacity-40" />

        {/* Right-anchored hero artwork — glowing legal scale meets digital circuits.
            Faded into the background with a left→right mask so the headline stays crisp. */}
        <motion.div
          initial={{ opacity: 1, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          className="hidden sm:block absolute top-[8%] right-[-6%] w-[62vw] max-w-[900px] aspect-square"
          style={{
            WebkitMaskImage:
              "radial-gradient(ellipse 60% 55% at 58% 50%, #000 62%, transparent 100%)",
            maskImage:
              "radial-gradient(ellipse 60% 55% at 58% 50%, #000 62%, transparent 100%)",
          }}
        >
          <motion.img
            src={imgReady ? HERO_ART : undefined}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            className="w-full h-full object-cover mix-blend-screen opacity-85"
            animate={{ scale: [1, 1.04, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          {/* Blue-cast overlay to lock the image into the brand palette */}
          <div className="absolute inset-0 bg-[#0052FF] mix-blend-color opacity-40" />
          {/* Soft focus glow behind the scale */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_50%,rgba(0,82,255,0.35),transparent_55%)]" />
        </motion.div>

        {/* Circuit signals converging into the artwork — kept as an accent */}
        <div className="absolute inset-0 opacity-45 hidden sm:block">
          <CircuitNetwork />
        </div>

        <div className="absolute inset-0 ambient-glow" />
        {/* Left→right darkening ensures headline legibility */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#030712] via-[#030712]/85 to-[#030712]/10" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#030712]/30 via-transparent to-[#030712]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-28">
        <motion.div
          initial={{ opacity: 1, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex items-center gap-2 mb-8 font-mono-tech text-xs tracking-[0.25em] uppercase text-[#3B82F6]"
        >
          <ShieldCheck size={14} strokeWidth={2} />
          {t.hero.overline}
        </motion.div>

        <h1 data-testid="hero-title" className="font-display font-extrabold tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-8xl text-white">
          {[t.hero.l1, t.hero.l2, t.hero.l3].map((l, i) => (
            <span key={i} className="reveal-mask">
              <motion.span custom={i} variants={line} initial="hidden" animate="show" className="block">
                {i === 2 ? <span className="text-[#3B82F6]">{l}</span> : l}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 1, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-8 max-w-xl text-base md:text-lg text-gray-400 leading-relaxed"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 1, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-assessment"
            onClick={() => scrollTo("assessment")}
            className="trace-btn group inline-flex items-center gap-2 bg-[#0052FF] hover:bg-[#2563EB] text-white font-medium px-7 py-3.5 rounded-sm transition-colors duration-200"
          >
            {t.hero.ctaPrimary}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-200" />
          </button>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-contact"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 text-white font-medium px-7 py-3.5 rounded-sm transition-colors duration-200"
          >
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <CraDeadlineCountdown />
      </div>
    </section>
  );
};
